import { cn } from '@/lib/utils';

/**
 * Smallest rendered share of the meter (%). Keeps a tiny-but-real filled
 * segment visible instead of collapsing it to an invisible sliver.
 */
const METER_MIN_VISIBLE_SHARE = 4;

// Filled segment reuses the ComparisonMeter positive palette (emerald-500/600);
// the unfilled remainder is striped amber over the track token so it reads as
// "not there yet" on both themes (mirrors the striped "left to track" segments
// of the time-tracking unified summary bar). Amber values match Tailwind's
// amber-500 (light) / amber-600 (dark) used by ComparisonMeter.
const REMAINDER_STRIPES_CLASS =
  '[background-image:repeating-linear-gradient(45deg,#f59e0b_0_5px,hsl(var(--track-empty))_5px_10px)] dark:[background-image:repeating-linear-gradient(45deg,#d97706_0_5px,hsl(var(--track-empty))_5px_10px)]';
const REMAINDER_SWATCH_CLASS =
  '[background-image:repeating-linear-gradient(45deg,#f59e0b_0_3px,hsl(var(--track-empty))_3px_6px)] dark:[background-image:repeating-linear-gradient(45deg,#d97706_0_3px,hsl(var(--track-empty))_3px_6px)]';

export interface ProgressMeterSide {
  /** Direct label next to the meter dot, e.g. "Tracked". */
  label: string;
  amount: number;
}

interface ProgressMeterProps {
  /**
   * Filled fraction of the track, 0–1 (clamped). Null renders an empty
   * neutral track (no meaningful ratio, e.g. nothing booked).
   */
  progress: number | null;
  /** The solid (achieved) side, e.g. tracked / approved hours. */
  filled: ProgressMeterSide;
  /** The striped (remaining/target) side, e.g. booked total / awaiting hours. */
  track: ProgressMeterSide;
  /** Formats an amount for display, e.g. `250 → "250h"`. */
  formatAmount: (amount: number) => string;
}

/**
 * Progress-style meter for stat cards: ONE track filled left-to-right by the
 * achieved share, with the unfilled remainder striped. Unlike `ComparisonMeter`
 * (two proportional segments), the fill directly encodes the headline ratio —
 * 100% renders a fully filled bar, not a 50/50 split.
 */
export const ProgressMeter = ({ progress, filled, track, formatAmount }: ProgressMeterProps) => {
  const clampedShare =
    progress === null
      ? null
      : Math.min(100, Math.max(progress * 100, progress > 0 ? METER_MIN_VISIBLE_SHARE : 0));

  return (
    <div className="mt-auto flex flex-col gap-1.5">
      <div className="flex h-2 overflow-hidden rounded-full" aria-hidden>
        {clampedShare === null ? (
          <div className="flex-1 bg-border" />
        ) : (
          <>
            {clampedShare > 0 && (
              <div
                className="rounded-full bg-emerald-500 dark:bg-emerald-600"
                style={{ width: `${clampedShare}%` }}
              />
            )}
            {clampedShare < 100 && <div className={cn('flex-1', REMAINDER_STRIPES_CLASS)} />}
          </>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 text-xs">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-full bg-emerald-500 dark:bg-emerald-600" />
          <span className="truncate text-muted-foreground">{filled.label}</span>
          <span className="whitespace-nowrap font-medium tabular-nums">
            {formatAmount(filled.amount)}
          </span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <span
            className={cn(
              'h-2 w-2 shrink-0 rounded-full border border-black/5',
              REMAINDER_SWATCH_CLASS
            )}
          />
          <span className="truncate text-muted-foreground">{track.label}</span>
          <span className="whitespace-nowrap font-medium tabular-nums">
            {formatAmount(track.amount)}
          </span>
        </span>
      </div>
    </div>
  );
};
